import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const bulkActions = [
  ["add_monday", "Add selected to Monday Vigil"],
  ["add_active", "Add to Active Prayer List"],
  ["remove_active", "Remove from Active List"],
  ["mark_still_held", "Mark Still Being Held"],
  ["request_update", "Request Update"],
  ["archive", "Archive"],
];

export default function AdminPrayerCircle() {
  const [prayers, setPrayers] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState("");
  const [view, setView] = useState("all"); // all | candidates | active | still
  const [action, setAction] = useState("add_monday");

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (view === "candidates") params.candidates_monday = true;
    else if (view === "active") { params.in_active_list = true; params.moderation_status = "approved"; }
    else if (view === "still") params.status = "Still Being Held";
    else params.moderation_status = "approved";
    api.get("/admin/prayers", { params }).then(({ data }) => { setPrayers(data.prayers); setSel(new Set()); });
  }, [q, view]);
  useEffect(load, [view]);

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel((s) => s.size === prayers.length ? new Set() : new Set(prayers.map((p) => p.id)));

  const runBulk = async () => {
    if (sel.size === 0) { toast("Select at least one prayer."); return; }
    const { data } = await api.post("/admin/prayers/bulk", { prayer_ids: [...sel], action });
    toast(data.message);
    load();
  };

  return (
    <div className="p-6 md:p-10">
      <p className="mono-label mb-3">Master Working List</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">Prayer Circle</h1>
      <p className="font-body text-ink-secondary mt-4 leading-relaxed max-w-2xl">Select any number of prayers and act on them together. You make every final selection — the software never chooses for you.</p>

      <div className="flex flex-wrap gap-2 mt-8">
        {[["all", "All approved"], ["candidates", "Candidates for Monday"], ["active", "Active list"], ["still", "Still being held"]].map(([k, label]) => (
          <button key={k} onClick={() => setView(k)} className={`mono-label px-3 py-2 ${view === k ? "bg-ink text-paper" : "border border-ink/25"}`} data-testid={`view-${k}`}>{label}</button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name, keyword, location" className="bg-transparent border border-ink/25 px-3 py-2 font-body text-ink outline-none flex-1 min-w-[200px]" data-testid="circle-search" />
        <button onClick={load} className="btn-ink">Search</button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-6 p-4 border border-ink/15 bg-paper-secondary sticky top-0 z-10">
        <span className="mono-label">{sel.size} selected</span>
        <select value={action} onChange={(e) => setAction(e.target.value)} className="bg-paper border border-ink/25 px-3 py-2 font-body text-ink outline-none" data-testid="bulk-action-select">
          {bulkActions.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <button onClick={runBulk} className="btn-solid" data-testid="bulk-apply">Apply to selected</button>
      </div>

      <div className="mt-6 overflow-x-auto border border-ink/15">
        <table className="w-full text-left border-collapse min-w-[900px]">
          <thead>
            <tr className="bg-paper-tertiary mono-label">
              <th className="p-3"><input type="checkbox" checked={sel.size === prayers.length && prayers.length > 0} onChange={toggleAll} className="w-4 h-4 accent-ink" data-testid="select-all" /></th>
              <th className="p-3">No.</th>
              <th className="p-3">Name</th>
              <th className="p-3">Category</th>
              <th className="p-3">Status</th>
              <th className="p-3">Vis.</th>
              <th className="p-3">Mon.</th>
              <th className="p-3">Held</th>
              <th className="p-3">Last Mon.</th>
            </tr>
          </thead>
          <tbody>
            {prayers.map((p) => (
              <tr key={p.id} className={`border-t border-ink/10 font-body text-ink ${sel.has(p.id) ? "bg-paper-secondary" : "bg-paper"}`} data-testid={`circle-row-${p.id}`}>
                <td className="p-3"><input type="checkbox" checked={sel.has(p.id)} onChange={() => toggle(p.id)} className="w-4 h-4 accent-ink" data-testid={`select-${p.id}`} /></td>
                <td className="p-3 font-mono text-xs">{p.prayer_number ? String(p.prayer_number).padStart(6, "0") : "—"}</td>
                <td className="p-3">{p.display_name || p.title}</td>
                <td className="p-3 text-sm">{p.category}</td>
                <td className="p-3 text-sm">{p.status}</td>
                <td className="p-3 text-sm">{p.is_public ? "Public" : "Private"}</td>
                <td className="p-3 text-sm">{p.consent_monday ? "✓" : "—"}</td>
                <td className="p-3 text-sm">{p.holders_count}</td>
                <td className="p-3 font-mono text-xs">{p.last_included_monday ? new Date(p.last_included_monday).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {prayers.length === 0 && <div className="p-12 text-center font-body text-ink-tertiary italic">No prayers in this view.</div>}
      </div>
    </div>
  );
}
